import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faPhone } from "@fortawesome/free-solid-svg-icons";

import { colleges, courseCount } from "../data/colleges";
import BannerWing from "./BannerWing";
import { useReveal } from "../lib/motion";
import healthcareBanner from "../assets/images/banners/healthcare.jpg";
import managementLawBanner from "../assets/images/banners/management-law.jpg";
import "./Carousel.css";

/* ---------------------------------------------------------------------------
   The hero is the institute's own banner artwork, turning over.

   The two banners already carry their own headline, course lists and
   accreditation marks, so they are shown whole — never cropped — and the page
   adds only what they don't: the CTAs and the four college links underneath.

   Between them the banners cover all four colleges (healthcare covers Medical
   Block and Allied Healthcare; the second covers Management and Law), so
   hovering a college link brings up the banner that speaks for it.
--------------------------------------------------------------------------- */

const BANNERS = [
  {
    id: "healthcare",
    src: healthcareBanner,
    alt:
      "From future caregivers to future leaders in healthcare — B.Sc Nursing, GNM and ANM under the Medical Block, and B.Sc Radiology & Imaging Technology, Operation Theatre Technology, Medical Lab Science and Optometry under Allied Healthcare.",
    covers: ["medical", "allied-healthcare"],
  },
  {
    id: "management-law",
    src: managementLawBanner,
    alt:
      "Choose the right path for your future — BBA, BCA, B.Voc Fashion Designer and Garment Technology and B.Voc Beauty Therapist under Management, and BA LLB and LLB under Law.",
    covers: ["management", "law"],
  },
];

const HOLD = 6000;

const Hero = () => {
  const [barRef, shown] = useReveal({ threshold: 0.1 });
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % BANNERS.length), HOLD);
    return () => clearInterval(t);
  }, [paused]);

  const showBannerFor = (slug) => {
    const n = BANNERS.findIndex((b) => b.covers.includes(slug));
    if (n >= 0) setI(n);
  };

  return (
    <section className="hero">
      <div
        className="banner-stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {BANNERS.map((b, n) => (
          <figure className={`banner ${n === i ? "is-on" : ""}`} key={b.id} aria-hidden={n !== i}>
            {/* the artwork holds its own size; the wings absorb whatever the
                viewport has spare, so the raster is never stretched */}
            <BannerWing variant={b.id} side="left" />
            <img src={b.src} alt={b.alt} />
            <BannerWing variant={b.id} side="right" />
          </figure>
        ))}

        <div className="banner-rail">
          {BANNERS.map((b, n) => (
            <button
              key={b.id}
              className={`banner-pip ${n === i ? "is-on" : ""}`}
              onClick={() => setI(n)}
              aria-label={`Show banner ${n + 1} of ${BANNERS.length}`}
              aria-current={n === i}
            />
          ))}
        </div>
      </div>

      <div ref={barRef} className={`hero-bar reveal ${shown ? "is-visible" : ""}`}>
        <div className="container hero-bar-inner">
          <p className="hero-strap">
            <strong>Four colleges. One seal.</strong>
            Nursing, allied healthcare, management and law — {courseCount} courses
            with supervised practice from the very first year.
          </p>

          <div className="hero-actions">
            <a href="/admissions" className="btn btn--primary">
              Apply for admission
              <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <a href="/home#colleges" className="btn btn--ghost">
              Explore courses
            </a>
            <a
              href="tel:+919899923387"
              className="hero-call"
              aria-label="Call admissions on +91-9899923387"
              title="+91-9899923387"
            >
              <FontAwesomeIcon icon={faPhone} />
            </a>
          </div>
        </div>

        <div className="container">
          {/* the college links also steer the banner behind them */}
          <ul className="hero-colleges" aria-label="Our colleges">
            {colleges.map((c) => (
              <li key={c.slug} style={{ "--tint": `var(--v-${c.slug})` }}>
                <a
                  href={`/colleges/${c.slug}`}
                  className={`hero-college seal-type ${
                    BANNERS[i].covers.includes(c.slug) ? "is-on" : ""
                  }`}
                  onMouseEnter={() => {
                    showBannerFor(c.slug);
                    setPaused(true);
                  }}
                  onMouseLeave={() => setPaused(false)}
                >
                  {c.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Hero;
